import {
  useCallback,
  useEffect,
  useState,
} from 'react';

import { ZERO_ADDRESS } from '@/collocate'; // 常量，表示零地址（无效地址）
import useFiboChainRpc
  from '@/store/builtInRpc/useFiboChainRpc'; // 获取只读的 factory 合约实例

/**
 * 自定义 Hook：用于根据两个 token 地址查询对应的 LP 合约地址
 * @param tokenA - Token A 的地址
 * @param tokenB - Token B 的地址
 * @returns { getLpAddress, getLpAddressLod, lpAddress }
 */
const useLpAddress = (tokenA: string, tokenB: string) => {
    const { factoryAbiReadOnly } = useFiboChainRpc(); // 获取只读的 Factory 合约方法（getPair）

    const [getLpAddressLod, setLoading] = useState(false); // 查询加载状态
    const [lpAddress, setLpAddress] = useState(''); // 查询结果：LP 地址

    /**
     * 查询 LP 地址方法
     * - 调用 Factory 合约的 getPair 方法获取 LP 地址
     * - 如果返回 ZERO_ADDRESS，说明该交易对没有 LP
     */
    const getLpAddress = useCallback(async () => {
        if (factoryAbiReadOnly && tokenA && tokenB) {
            try {
                setLoading(true); // 设置加载状态为 true
                const lp = await factoryAbiReadOnly.getPair(tokenA, tokenB); // 查询 LP 地址
                if (lp === ZERO_ADDRESS) {
                    setLpAddress(''); // 无 LP，设为空字符串
                } else {
                    setLpAddress(lp); // 有 LP，设置为返回地址
                }
            } catch (e) {
                console.log('useLpAddress error:', e);
                setLpAddress(''); // 出错时，设为空字符串
            } finally {
                setLoading(false); // 查询结束，关闭加载状态
            }
        } else {
            setLpAddress(''); // 如果参数不全，设为空字符串
        }
    }, [factoryAbiReadOnly, tokenA, tokenB]);

    // 当 getLpAddress 函数依赖发生变化时自动调用（即 tokenA/tokenB/factoryAbiReadOnly 改变时自动查询）
    useEffect(() => {
        getLpAddress();
    }, [getLpAddress]);

    // 返回查询函数、加载状态、LP 地址
    return {
        getLpAddress,       // 主动触发查询的函数
        getLpAddressLod,    // 是否处于加载状态
        lpAddress           // 查询到的 LP 地址
    };
};

export default useLpAddress;